import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrereqBlock from '../components/PrereqBlock.js';
import ManifestBlock from '../components/ManifestBlock.js';
import type { Spec } from '../api.js';

interface DraftResponse {
  ok: boolean;
  spec?: Spec;
  raw_response?: string;
  validation_errors?: string[];
  error?: string;
  tokens?: { input: number; output: number };
}

type Phase = 'form' | 'drafting' | 'preview' | 'invalid' | 'error';

export default function DropIdea() {
  const [idea, setIdea] = useState('');
  const [contextNotes, setContextNotes] = useState('');
  const [showContext, setShowContext] = useState(false);
  const [phase, setPhase] = useState<Phase>('form');
  const [draft, setDraft] = useState<DraftResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  async function draftIt() {
    setPhase('drafting');
    setDraft(null);
    try {
      const res = await fetch('/api/planner/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idea: idea.trim(),
          context_notes: contextNotes.trim() || undefined,
        }),
      });
      const body: DraftResponse = await res.json();
      setDraft(body);
      if (body.ok) setPhase('preview');
      else if (body.validation_errors) setPhase('invalid');
      else setPhase('error');
    } catch (e) {
      setDraft({ ok: false, error: (e as Error).message });
      setPhase('error');
    }
  }

  function discard() {
    setDraft(null);
    setPhase('form');
  }

  async function createTrack() {
    if (!draft?.spec) return;
    setCreating(true);
    try {
      const res = await fetch('/api/tracks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft.spec),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? `create failed (${res.status})`);
      navigate(`/tracks/${body.track.id}`);
    } catch (e) {
      alert(`Create failed: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  }

  let clarifications: string[] = [];
  if (draft?.raw_response) {
    try {
      const cleaned = draft.raw_response
        .trim()
        .replace(/^```(?:json)?\s*/i, '')
        .replace(/\s*```$/, '');
      const parsed = JSON.parse(cleaned);
      if (Array.isArray(parsed?.clarifications_needed)) {
        clarifications = parsed.clarifications_needed.filter(
          (s: unknown): s is string => typeof s === 'string'
        );
      }
    } catch {
      // ignore parse errors — clarifications stay empty
    }
  }

  return (
    <div>
      {phase !== 'preview' && (
        <>
          <div className="group-title">Drop an idea</div>
          <p className="muted" style={{ marginTop: 0 }}>
            Describe what you want built. Planning Claude will draft a full spec for your review.
          </p>
          <textarea
            className="spec-input"
            style={{ height: 200 }}
            placeholder="What do you want to build? Be as specific or as rough as you like."
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            spellCheck={false}
          />
          {!showContext ? (
            <div className="actions" style={{ marginTop: 8 }}>
              <button
                className="btn-outline-conductor"
                onClick={() => setShowContext(true)}
                type="button"
              >
                + Add context
              </button>
            </div>
          ) : (
            <textarea
              className="spec-input"
              style={{ height: 120, marginTop: 10 }}
              placeholder="Anything Planning Claude should know? (existing features it touches, prior decisions, etc.)"
              value={contextNotes}
              onChange={(e) => setContextNotes(e.target.value)}
              spellCheck={false}
            />
          )}
          <div className="actions">
            <button
              className="btn-conductor"
              disabled={phase === 'drafting' || idea.trim().length < 5}
              onClick={draftIt}
            >
              {phase === 'drafting' ? 'Drafting… (10–30s)' : 'Draft spec'}
            </button>
          </div>
        </>
      )}

      {phase === 'error' && draft && (
        <div className="errors" style={{ marginTop: 12 }}>
          <strong>Draft failed</strong>
          <div>{draft.error}</div>
          {draft.raw_response && (
            <details style={{ marginTop: 8 }}>
              <summary>raw response</summary>
              <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{draft.raw_response}</pre>
            </details>
          )}
          <div className="actions">
            <button className="btn-outline-conductor" onClick={draftIt}>Retry</button>
          </div>
        </div>
      )}

      {phase === 'invalid' && draft && (
        <div className="errors" style={{ marginTop: 12 }}>
          <strong>Spec failed validation</strong>
          {draft.validation_errors && (
            <ul>
              {draft.validation_errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          {draft.raw_response && (
            <details style={{ marginTop: 8 }}>
              <summary>raw response</summary>
              <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{draft.raw_response}</pre>
            </details>
          )}
          <div className="actions">
            <button className="btn-outline-conductor" onClick={draftIt}>Retry</button>
          </div>
        </div>
      )}

      {phase === 'preview' && draft?.spec && (
        <div className="detail">
          <div className="badge-row">
            <span className="badge badge-gray">{draft.spec.risk_level} risk</span>
            {draft.tokens && (
              <span className="badge badge-gray code">
                {draft.tokens.input}→{draft.tokens.output} tok
              </span>
            )}
          </div>
          <h2>{draft.spec.title}</h2>
          <div className="one-line">{draft.spec.one_line}</div>

          {clarifications.length > 0 && (
            <div className="section prereq-block">
              <h3>Planning Claude wants to confirm</h3>
              <ul>
                {clarifications.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          <PrereqBlock prereqs={draft.spec.prereqs ?? []} />
          <ManifestBlock spec={draft.spec} />

          <div className="section">
            <h3>Rollback</h3>
            <div className="kv">
              <strong>tag:</strong>
              <span className="code">{draft.spec.rollback.git_tag}</span>
            </div>
            <div className="kv">
              <strong>side effects:</strong>
              <span>{draft.spec.rollback.side_effects}</span>
            </div>
          </div>

          <div className="section">
            <h3>Success criteria</h3>
            <ul>
              {draft.spec.success_criteria.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>

          <details style={{ marginBottom: 12 }}>
            <summary>raw response</summary>
            <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{draft.raw_response}</pre>
          </details>

          <div className="actions">
            <button className="btn-conductor" onClick={createTrack} disabled={creating}>
              {creating ? 'Creating…' : 'Create track'}
            </button>
            <button className="btn-outline-conductor" onClick={discard} disabled={creating}>
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
